/* eslint-disable react/jsx-no-useless-fragment */
import {
  EffectiveDateType,
  IOfferingRes,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  IRateResBaseSchema,
  OfferingTypesEnum,
  ProductTypeEnum,
  QuoteAmendmentVersionEnum,
  QuoteItemAmendmentStatusEnum,
  QuoteOfferingProps,
  QuoteOfferingReqSchema,
  QuoteOfferingState,
  QuoteStatusEnum,
  QuoteTypeEnum,
  RateBillingFrequencyEnum,
  RateUsageBillingFrequencyEnum,
  ScheduleChangeState,
} from '@monetize/types/app';
import {
  calculateDateDifference,
  formatCurrency,
  toDateOnly,
  toDateShort,
} from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addYears } from 'date-fns/addYears';
import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { getDaysInMonth } from 'date-fns/getDaysInMonth';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import clamp from 'lodash/clamp';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import React, { FC, ReactNode } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MText,
} from '../../../../../components/Monetize';
import { RATE_BILLING_FREQUENCY_MAP } from '../../../../../constants/offerings';
import {
  DELAYED_BILLING_QUOTE_START_SOURCE,
  QUOTE_GRID_TEMPLATE_COLUMNS,
  SCHEDULED_OFFERING_OPTIONS,
} from '../../../../../constants/quotes';
import { QuoteDataById } from '../../../../../hooks/useQuote';
import Sentry from '../../../../../services/sentry';
import { getQuoteOfferingAmendStatusIsRemoved } from '../../../../../utils';
import { getQuoteOfferingDiscountAvailability } from '../../../../../utils/quotes';
import QuoteOfferingRateRightEl from './QuoteOfferingRateRightEl';

/**
 * Separator between an offering and rate
 */
export const ItemRateSep = ({ isDisabled }: { isDisabled?: boolean }) => {
  return (
    <MFlex
      alignItems="center"
      justifyContent="center"
      borderTop="1px"
      borderBottom="1px"
      width="1px"
      backgroundColor={isDisabled ? 'tGray.back' : 'transparent'}
      borderColor="tGray.lightPurple"
    >
      <MBox height="4" width="1px" backgroundColor="tGray.lightPurple" />
    </MFlex>
  );
};

export const getRateRightElement = ({
  value,
  offeringRatesById,
}: {
  value: string;
  offeringRatesById: Record<string, IRateResBaseSchema>;
}) => {
  const foundRate = offeringRatesById[value];

  if (!foundRate) {
    return null;
  }

  return {
    width: 12,
    content: <QuoteOfferingRateRightEl rate={foundRate} />,
  };
};

/**
 * Whether the parent rate section and every scheduled-change child rate
 * section are expanded. Children without an explicit entry default to open.
 */
export const getAreAllRatesExpanded = ({
  isParentRateOpen,
  childQuoteOfferings,
  childRateOpenStates,
}: {
  isParentRateOpen: boolean;
  childQuoteOfferings: Pick<IQuoteOfferingRespSchema, 'id'>[];
  childRateOpenStates: Partial<Record<string, boolean>>;
}): boolean =>
  isParentRateOpen &&
  childQuoteOfferings.every(({ id }) => childRateOpenStates[id] ?? true);

/**
 * Distinct rate ids across the quote's offerings (parents and scheduled-change
 * segments alike), in first-seen order. Used to warm the rate cache up front.
 */
export const getQuoteOfferingRateIds = (
  quoteOfferings: Pick<IQuoteOfferingRespSchema, 'rateId'>[],
): string[] => {
  const rateIds = new Set<string>();
  quoteOfferings.forEach(({ rateId }) => {
    if (rateId) {
      rateIds.add(rateId);
    }
  });
  return Array.from(rateIds);
};

export interface CollapsedRateSummary {
  /** The segment's date range, or its single date when one-time */
  dateLabel: string;
  /** Length of the segment; empty when there is no range */
  durationLabel: string;
  productCountLabel: string;
}

/**
 * Labels for a collapsed rate row. The range starts where the expanded row's
 * Start Date field does, with its length beneath; a one-time segment has a
 * single date and no length.
 */
export const getCollapsedRateSummary = ({
  startDate,
  endDate,
  isOneTime,
  itemCount,
}: {
  startDate?: string | null;
  endDate?: string | null;
  isOneTime: boolean;
  itemCount: number;
}): CollapsedRateSummary => {
  const productCountLabel = `${itemCount} ${
    itemCount === 1 ? 'product' : 'products'
  }`;

  if (!startDate) {
    return { dateLabel: '', durationLabel: '', productCountLabel };
  }
  if (isOneTime || !endDate) {
    return {
      dateLabel: toDateShort(startDate),
      durationLabel: '',
      productCountLabel,
    };
  }

  return {
    dateLabel: `${toDateShort(startDate)} – ${toDateShort(endDate)}`,
    durationLabel: calculateDateDifference(startDate, endDate).humanized,
    productCountLabel,
  };
};

/**
 * Offering toggle button
 */
export const OfferingToggleButton = ({
  isOpen,
  setIsOpen,
  offeringIndex,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  offeringIndex: number;
}) => {
  return (
    <MCustomIconButton
      btnSize={3}
      containerSize={8}
      icon={isOpen ? FaChevronDown : FaChevronRight}
      aria-label="Toggle offering"
      onClick={() => setIsOpen(!isOpen)}
      data-testid={`offering-toggle-btn-${offeringIndex}`}
    />
  );
};

/**
 * Rate toggle button
 */
export const RateToggleButton = ({
  isOpen,
  setIsOpen,
  offeringIndex,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  offeringIndex: number;
}) => {
  return (
    <MCustomIconButton
      btnSize={3}
      containerSize={6}
      minW="6"
      icon={isOpen ? FaChevronDown : FaChevronRight}
      aria-label="Toggle rate"
      onClick={() => setIsOpen(!isOpen)}
      data-testid={`rate-toggle-btn-${offeringIndex}`}
    />
  );
};

/**
 * If collapsed, will show rate names and dates, otherwise will show child content
 * If removed, will render removed text
 * If open and active, will render offering text
 */
export const QuoteOfferingCollapsibleRate: FC<{
  isOpen: boolean;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  rateName: string;
  isOfferingEndBeforeContract: boolean;
  offeringEndDate?: string;
  isRemoved: boolean;
  isNewlyAddedOffering: boolean;
  children: ReactNode;
}> = ({
  isOpen,
  quoteOffering,
  rateName,
  isOfferingEndBeforeContract,
  offeringEndDate,
  isRemoved,
  isNewlyAddedOffering,
  children,
}) => {
  if (isRemoved) {
    <MFlex minH="8" alignItems="center">
      {isRemoved && (
        <MText color="#000" fontStyle="italic" mr="2">
          Removed
        </MText>
      )}
      <MText fontWeight="bold" mr={1} mt={1} lineClamp={1}>
        {quoteOffering?.offeringName}
        {quoteOffering?.rateName ? ' - ' : ''}
        {quoteOffering?.rateName}
      </MText>
    </MFlex>;
  }

  const isOfferingOnetime =
    quoteOffering?.billingFrequency === RateBillingFrequencyEnum.ONETIME;

  const isSubscriptionIdPresent = !!quoteOffering?.subscriptionId;

  if (isOpen && quoteOffering && rateName) {
    return (
      <MFlex minH="8" alignItems="center">
        {isRemoved && (
          <MText color="#000" fontStyle="italic" mr="2">
            Removed
          </MText>
        )}
        <MText fontWeight="600" color="fg.strong">
          {quoteOffering.offeringName}
        </MText>
        {isOfferingEndBeforeContract && !!offeringEndDate && (
          <>
            {isOfferingOnetime &&
            !isSubscriptionIdPresent &&
            !isNewlyAddedOffering ? (
              <MText fontSize="xxs" color="tOrange.tangerine" ml="4">
                Existing One-time Offerings may not be Amended. If you need to
                remove it, consider issuing a Credit Note.
              </MText>
            ) : (
              !isOfferingOnetime && (
                <MText fontSize="xxs" color="tOrange.tangerine" ml="4">
                  Deleted as of{' '}
                  {toDateShort(
                    addDays(parseISO(offeringEndDate), 1),
                    'userTimezone',
                  )}
                </MText>
              )
            )}
          </>
        )}
      </MFlex>
    );
  }

  if (isOpen || !quoteOffering || !rateName) {
    return children;
  }

  return (
    <MBox pt="0.2rem">
      <MText fontSize="md" fontWeight={600}>
        {rateName} {toDateShort(quoteOffering.startDate)} -{' '}
        {toDateShort(quoteOffering.endDate)}
      </MText>
    </MBox>
  );
};

/**
 * Ensure that the provided value is within the provided range.
 * Otherwise, clamp value to lower or upper bound.
 * If value is not provided, default to lower.
 * If lower is not provided, default to 0.
 * If upper is not provided, default to Infinity (in which case value would always be returned).
 */
export function clampValueToRange(value: number, min = 0, max = Infinity) {
  min = isNumber(min) && !isNaN(min) ? min : 0;
  max = isNumber(max) && !isNaN(max) ? max : Infinity;
  value = value ?? min;
  value = isNumber(value) && !isNaN(value) ? value : min;

  return clamp(value, min, max);
}

/**
 * Rebuilds an offering's update request from its current server state, for
 * flows that re-save an offering as-is (restoring a removed offering and its
 * scheduled changes).
 *
 * Spreads the whole response rather than listing the shared fields: every
 * field the request and response have in common carries through untouched,
 * and `parse` strips the response-only ones. Hand-listing them silently
 * dropped `options` and `endDate`, which the API then reset (BP-18551).
 *
 * @param quoteOffering IQuoteOfferingRespSchema
 * @returns IQuoteOfferingReqSchema
 */
export const prepareQuoteOfferingRequestDataFromResponse = (
  quoteOffering: IQuoteOfferingRespSchema,
) => {
  const {
    startDate,
    items,
    parentQuoteOfferingId,
    discounts: offeringDiscounts,
  } = quoteOffering;
  return QuoteOfferingReqSchema.parse({
    ...quoteOffering,
    discountIds: offeringDiscounts.map(({ id: discountId }) => discountId),
    items: items.map((item) => ({
      ...item,
      customPrice: item.customPriceId
        ? {
            id: item.customPriceId,
            unitAmount: item.unitPrice || 0,
          }
        : undefined,
      quantity: item.quantity,
      customDiscountAmountOrPercent: item.customDiscountAmountOrPercent,
      customDiscountType: item.customDiscountType,
    })),
    schedule: parentQuoteOfferingId
      ? {
          startDate,
          parentQuoteOfferingId,
        }
      : undefined,
  });
};

export const isQuoteOfferingChanged = (
  quoteOffering: IQuoteOfferingRespSchema,
  quoteDataOnInitialLoad: QuoteDataById,
) => {
  // If quote offering does not exist in initial data, it is newly added and is "changed" no matter what BE reports
  if (!quoteDataOnInitialLoad.quoteOfferings[quoteOffering.id]) {
    return true;
  }
  return quoteOffering.items.some((item) => {
    const dataWasChangedAtSomePoint = quoteDataOnInitialLoad.quoteItems[item.id]
      ? quoteDataOnInitialLoad.quoteItems[item.id].amendmentStatus !==
        QuoteItemAmendmentStatusEnum.NO_CHANGE
      : false;
    return (
      dataWasChangedAtSomePoint ||
      item.amendmentStatus !== QuoteItemAmendmentStatusEnum.NO_CHANGE
    );
  });
};

/**
 * Processes a quote offering and returns various indicators related to its state.
 *
 * @param quoteOffering - The offering associated with the quote.
 * @param index - The index of the offering within the quote.
 * @param childQuoteOfferings - An array of child offerings if the current offering has any.
 * @param quote - The full quote object containing all offerings and details.
 * @param isChildOffering - A boolean indicating if the current offering is a child offering.
 * @param parentQuoteOffering - A reference to the parent offering if the current offering is a child.
 * @param quoteDataOnInitialLoad - The initial data of the quote when it was first loaded.
 *
 * @returns {Object} An object with the following properties:
 *  - newlyAddedOffering: `boolean` - Indicates if the offering was added in the current quote.
 *  - isNewQuoteOfferingForm: `boolean` - Indicates if this is a new offering form.
 *  - isRemoved: `boolean` - Indicates if the offering has been removed.
 *  - isAmendment: `boolean` - Indicates if the quote represents an amendment.
 *  - isRenewal: `boolean` - Indicates if the offering is a renewal.
 */
export const getQuoteOfferingState = ({
  quoteOffering,
  index: offeringIndex,
  childQuoteOfferings = [],
  quote,
  isChildOffering,
  parentQuoteOffering,
  quoteDataOnInitialLoad,
}: QuoteOfferingProps & {
  quote?: IQuoteRespSchema | null;
  quoteDataOnInitialLoad: QuoteDataById;
}): QuoteOfferingState => {
  // TEMPORARY don't allow removal of a scheduled offering on amendments
  const newlyAddedOffering = !!quoteOffering?.items.some(
    ({ amendmentStatus }) =>
      amendmentStatus === QuoteItemAmendmentStatusEnum.ADDED,
  );
  const isNewQuoteOfferingForm = offeringIndex === -1;
  const isLastSegment = childQuoteOfferings.length
    ? offeringIndex === childQuoteOfferings.length
    : true;

  const isRemoved = getQuoteOfferingAmendStatusIsRemoved(
    isChildOffering ? parentQuoteOffering : quoteOffering,
  );
  const isAmendment = quote?.type === QuoteTypeEnum.AMENDMENT;
  const isRenewal = quote?.type === QuoteTypeEnum.RENEWAL;
  const isDraft = quote?.status === QuoteStatusEnum.DRAFT;

  const isLocked = !!quoteOffering?.locked;
  const isReadOnlyAndNotEmptyOffering = isLocked && !isNewQuoteOfferingForm;

  const allowPriceCustomization = !isLocked;

  let bgColor = 'tWhite.titanWhite';
  if (isRemoved || isReadOnlyAndNotEmptyOffering) {
    bgColor = 'tGray.disabled';
  } else if (isChildOffering) {
    bgColor = 'tWhite.titanWhite';
  }

  let bgColorV2 = 'tWhite.base';
  if (isRemoved || isReadOnlyAndNotEmptyOffering) {
    bgColorV2 = 'tWhite.base';
  } else if (isChildOffering) {
    bgColorV2 = 'tWhite.base';
  }
  // indicates the offering (quoteOffering) was carried over from the original quote to an amended/renewed quote -- details of the offering
  // can be edited (like rate), and it can be removed, but it cannot be replaced with another offering so the select is disabled
  const isOfferingUnselectable = !!(
    quoteOffering?.subscriptionId &&
    [QuoteTypeEnum.AMENDMENT, QuoteTypeEnum.RENEWAL].includes(quote?.type!)
  );

  const gridColumns = QUOTE_GRID_TEMPLATE_COLUMNS;

  const { isQuoteOfferingDiscountAvailable } =
    getQuoteOfferingDiscountAvailability(quoteOffering);

  const isOfferingOnetime =
    quoteOffering?.billingFrequency === RateBillingFrequencyEnum.ONETIME;

  const lastSegment =
    (childQuoteOfferings || []).length > 0
      ? childQuoteOfferings[childQuoteOfferings.length - 1]
      : quoteOffering;

  const isLastSegmentNoChange = !!lastSegment?.items.every(
    (item) => item.amendmentStatus === QuoteItemAmendmentStatusEnum.NO_CHANGE,
  );

  const allOfferings = [
    isChildOffering ? parentQuoteOffering : quoteOffering,
    ...(childQuoteOfferings || []),
  ].filter((offering) => !!offering) as IQuoteOfferingRespSchema[];

  let isNoOfferingChanged = allOfferings.every((offering) =>
    offering.items.every(
      (item) =>
        item.amendmentStatus === QuoteItemAmendmentStatusEnum.NO_CHANGE &&
        (!item.amendmentStatusOverwrite ||
          item.amendmentStatusOverwrite ===
            QuoteItemAmendmentStatusEnum.NO_CHANGE),
    ),
  );

  // Check local state overrides for amendments to see if we can allow a scheduled change
  if (
    isNoOfferingChanged &&
    quote?.type === QuoteTypeEnum.AMENDMENT &&
    quote?.amendmentVersion === QuoteAmendmentVersionEnum.v2
  ) {
    // add additional checks to see if we can allow a scheduled change
    isNoOfferingChanged = allOfferings.every((offering) =>
      offering.items.every(
        (item) =>
          !quoteDataOnInitialLoad.quoteItems[item.id] ||
          quoteDataOnInitialLoad.quoteItems[item.id].amendmentStatus ===
            QuoteItemAmendmentStatusEnum.NO_CHANGE,
      ),
    );
  }

  const firstQuoteOffering = quoteOffering;
  const lastQuoteOffering =
    isChildOffering || childQuoteOfferings.length === 0
      ? quoteOffering
      : childQuoteOfferings[childQuoteOfferings.length - 1];

  const quoteOfferingGroupStartDate = firstQuoteOffering?.startDate;
  const quoteOfferingGroupEndDate = lastQuoteOffering?.endDate;

  const isUsingDelayedBilling = getIsUsingDelayedBilling(quote);

  return {
    newlyAddedOffering,
    isNewQuoteOfferingForm,
    isLastSegment,
    isRemoved,
    isAmendment,
    isRenewal,
    isDraft,
    isLocked,
    isReadOnlyAndNotEmptyOffering,
    allowPriceCustomization,
    gridColumns,
    isQuoteOfferingDiscountAvailable,
    bgColor,
    bgColorV2,
    isOfferingUnselectable,
    isOfferingOnetime,
    isLastSegmentNoChange,
    isNoOfferingChanged,
    quoteOfferingGroupStartDate,
    quoteOfferingGroupEndDate,
    isUsingDelayedBilling,
  };
};

export const hasUsageProducts = (
  quoteOffering: IQuoteOfferingRespSchema | undefined | null,
  offering: IOfferingRes | undefined | null,
) => {
  const selectedProducts = quoteOffering?.items?.map((item) => item.productId);

  return (
    offering?.products?.some(
      (product) =>
        product.productType === ProductTypeEnum.USAGE &&
        selectedProducts?.includes(product.id),
    ) ?? false
  );
};

export const getScheduleChangeState = ({
  isChildOffering,
  quoteOffering,
  quote,
  offering,
}: {
  isChildOffering: boolean;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  quote?: IQuoteRespSchema | null;
  offering?: IOfferingRes;
}): ScheduleChangeState => {
  const offeringRate = offering?.rates.find(
    (rate) => rate.id === quoteOffering?.rateId,
  );

  const isUsingDelayedBilling = getIsUsingDelayedBilling(quote);
  const isMinCommit = offering?.type === OfferingTypesEnum.MIN_COMMIT;
  const hasUsageProduct = hasUsageProducts(quoteOffering, offering);

  const startDate =
    isMinCommit || hasUsageProduct || isUsingDelayedBilling
      ? quote?.contractStartDate
      : quoteOffering?.startDate || quote?.contractStartDate;
  const endDate = quoteOffering?.endDate || quote?.contractEndDate;

  if (!startDate || !endDate) {
    Sentry.logger.error('getScheduleChangeState - missing required date', {
      startDate,
      endDate,
      quoteId: quote?.id,
      quoteOfferingId: quoteOffering?.id,
    });
    return {
      availableMonthSelectOptions: SCHEDULED_OFFERING_OPTIONS.filter(
        ({ value }) => isString(value),
      ),
      monthInterval: 1,
      scheduleChangeInitDate: new Date(),
    };
  }

  const scheduleChangeInitDate = parseISO(startDate);
  const scheduleChangeEndDate = parseISO(endDate);

  // How many months remain until the start of next schedule change or end of contract
  const remainingMonths = differenceInMonths(
    scheduleChangeEndDate,
    scheduleChangeInitDate,
  );

  let availableMonthSelectOptions: {
    title: string;
    value: EffectiveDateType;
  }[] = SCHEDULED_OFFERING_OPTIONS.filter(
    ({ value }) => isString(value) || value <= remainingMonths,
  );

  let monthInterval = 1;

  // If delayed billing, use annual billing frequency since we only allow ramps on annual basis
  const usageBillingFrequency = isUsingDelayedBilling
    ? RateUsageBillingFrequencyEnum.ANNUALLY
    : offeringRate?.usageBillingFrequency;

  const billingFrequencyDisplay =
    offeringRate &&
    usageBillingFrequency &&
    RATE_BILLING_FREQUENCY_MAP[usageBillingFrequency]() &&
    RATE_BILLING_FREQUENCY_MAP[usageBillingFrequency]()
      ? RATE_BILLING_FREQUENCY_MAP[usageBillingFrequency]()
      : null;

  if (isMinCommit || hasUsageProduct || isUsingDelayedBilling) {
    if (
      billingFrequencyDisplay?.month &&
      typeof billingFrequencyDisplay?.month === 'number'
    ) {
      monthInterval = billingFrequencyDisplay?.month;

      // Guard against a non-positive or invalid (NaN) window: an offering whose
      // end date precedes its start date yields a negative remainingMonths, and
      // `new Array(negativeOrNaN)` throws `RangeError: Invalid array length`.
      const rawIntervalCount = Math.floor(remainingMonths / monthInterval);

      if (!Number.isFinite(rawIntervalCount) || rawIntervalCount < 0) {
        Sentry.logger.warn(
          'getScheduleChangeState - invalid schedule-change window',
          {
            remainingMonths,
            monthInterval,
            startDate,
            endDate,
            quoteId: quote?.id,
            quoteOfferingId: quoteOffering?.id,
          },
        );
      }

      const intervalCount = Math.max(0, rawIntervalCount || 0);

      if (usageBillingFrequency === RateUsageBillingFrequencyEnum.ANNUALLY) {
        availableMonthSelectOptions = new Array(intervalCount)
          .fill(0)
          .map((_, index) => ({
            title: `+ ${index + 1} Year`,
            value: (index + 1) * monthInterval,
          }));
      } else {
        availableMonthSelectOptions = new Array(intervalCount)
          .fill(0)
          .map((_, index) => ({
            title: `+ ${(index + 1) * monthInterval} Month`,
            value: (index + 1) * monthInterval,
          }));
      }
    }
  }

  return {
    availableMonthSelectOptions,
    monthInterval,
    scheduleChangeInitDate,
  };
};
export const getTotalValues = (
  quote: IQuoteRespSchema,
  variableAmountIndicator: string,
) => {
  if (!quote?.incrementalPrices) {
    return null;
  }

  const formatValue = (value?: number | null) => {
    if (typeof value !== 'number') {
      return '';
    }
    return (
      formatCurrency(value, {
        currency: quote?.currency,
      }) + variableAmountIndicator
    );
  };
  return {
    subtotal: {
      value: formatValue(quote?.incrementalPrices?.absolute?.amount),
      priorValue: formatValue(quote?.incrementalPrices?.prior?.amount),
      amendmentValue: formatValue(quote.incrementalPrices?.incremental?.amount),
    },
    discount: {
      value: formatValue(-quote?.incrementalPrices?.absolute?.discountAmount),
      priorValue: formatValue(-quote?.incrementalPrices?.prior?.discountAmount),
      amendmentValue: formatValue(
        -quote?.incrementalPrices?.incremental?.discountAmount,
      ),
    },
    total: {
      value: formatValue(
        quote?.incrementalPrices?.absolute.amountAfterDiscount,
      ),
      priorValue: formatValue(
        quote?.incrementalPrices?.prior?.amountAfterDiscount,
      ),
      amendmentValue: formatValue(
        quote?.incrementalPrices?.incremental?.amountAfterDiscount,
      ),
    },
  };
};

export const getMonthsDiffInProration = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  const fromDate =
    typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const toDate = typeof endDate === 'string' ? parseISO(endDate) : endDate;
  const monthsDiff = differenceInCalendarMonths(toDate, fromDate);

  const daysDiff = differenceInDays(addMonths(fromDate, monthsDiff), toDate);
  const daysInEndMonth = getDaysInMonth(toDate);

  return monthsDiff - daysDiff / daysInEndMonth;
};

/**
 * Return the anniversaries spanning from start/end date
 * @param startDate
 * @param endDate
 * @param duration
 * @param billingAnchor - Optional billing anchor with day and month. If provided, the first anniversary is the contract start date, and subsequent anniversaries are based on the billing anchor.
 */
export const getAnniversaries = (
  startDate: string | Date,
  endDate: string | Date,
  duration = 'year',
  billingAnchor?: { day: number | null; month: number | null } | null,
) => {
  const anniversaryDates: string[] = [];

  const fromDate =
    typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const toDate = typeof endDate === 'string' ? parseISO(endDate) : endDate;

  // If billing anchor is provided and valid, use it for subsequent anniversaries
  const hasBillingAnchor =
    !!billingAnchor &&
    billingAnchor.day !== null &&
    billingAnchor.month !== null;

  if (hasBillingAnchor && duration === 'year') {
    // First anniversary is always the contract start date
    anniversaryDates.push(toDateOnly(fromDate, 'userTimezone'));

    // Calculate subsequent anniversaries based on billing anchor
    let currentYear = fromDate.getFullYear() + 1;
    const billingAnchorDay = billingAnchor!.day!;
    const billingAnchorMonth = billingAnchor!.month! - 1; // JavaScript months are 0-indexed

    // Normalize end date to start of day for consistent comparison
    const normalizedEndDate = startOfDay(toDate);

    while (currentYear <= toDate.getFullYear()) {
      // Create date with billing anchor day/month in the current year
      const anniversaryDate = startOfDay(
        new Date(currentYear, billingAnchorMonth, billingAnchorDay),
      );

      // Only add if it's before or equal to the end date
      if (anniversaryDate <= normalizedEndDate) {
        anniversaryDates.push(toDateOnly(anniversaryDate, 'userTimezone'));
      }

      currentYear += 1;
    }
  } else {
    // Original behavior: all anniversaries based on start date
    let currentDate = fromDate;
    while (currentDate < toDate) {
      anniversaryDates.push(toDateOnly(currentDate, 'userTimezone'));
      if (duration === 'year') {
        currentDate = addYears(currentDate, 1);
      }
    }
  }

  return anniversaryDates;
};

/**
 * Delayed billing (start date set by signature) shifts the whole contract, so every
 * recurring offering must start on an annual anniversary of the contract start.
 * One-time offerings are excluded, matching the backend `validateQuoteOfferingSpans`.
 */
export const getCanConfigureDelayedBilling = ({
  contractStartDate,
  contractEndDate,
  billingAnchor,
  quoteOfferings,
}: Pick<
  IQuoteRespSchema,
  'contractStartDate' | 'contractEndDate' | 'billingAnchor' | 'quoteOfferings'
>): boolean => {
  const annualAnniversaries = getAnniversaries(
    contractStartDate,
    contractEndDate,
    'year',
    billingAnchor,
  );

  return (
    quoteOfferings?.every(
      ({ startDate, billingFrequency }) =>
        billingFrequency === RateBillingFrequencyEnum.ONETIME ||
        annualAnniversaries.includes(startDate),
    ) ?? false
  );
};

/**
 * If it's exact X years of difference
 * @param startDate
 * @param endDate
 * @param duration
 */
export const isAnnualDifference = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  return getMonthsDiffInProration(startDate, endDate) % 12 === 0;
};

export const getIsUsingDelayedBilling = (quote?: IQuoteRespSchema | null) => {
  return (
    !!quote?.type &&
    quote?.type === QuoteTypeEnum.NEW &&
    !!quote?.startDateSource &&
    DELAYED_BILLING_QUOTE_START_SOURCE.has(quote?.startDateSource)
  );
};
